// src/services/healthService.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';

// Local type definition
interface ProcessedResult {
  success: boolean;
  data?: any;
  error?: {
    message: string;
    code?: string;
    type?: string;
  };
}

// Helper function to get auth token from localStorage
const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('access_token');
  } catch {
    return null;
  }
};

// Helper function to get auth headers
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  
  if (token) {
    return {
      'Authorization': `Bearer ${token}`
    };
  }
  
  return {};
};

export const healthService = {
  extractHealthMetrics: async (file: File, password?: string): Promise<ProcessedResult> => {
    const formData = new FormData();
    formData.append('file', file);
    
    if (password) {
      formData.append('password', password);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/extract`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders()
          // Don't set Content-Type for FormData - browser sets it with boundary
        },
        body: formData,
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('UNAUTHORIZED - Please log in again');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        // Handle specific password-related errors
        if (data.error?.message?.toLowerCase().includes('password')) {
          if (data.error.message.toLowerCase().includes('protected')) {
            throw new Error('PDF_PASSWORD_REQUIRED');
          } else if (data.error.message.toLowerCase().includes('invalid')) {
            throw new Error('INVALID_PASSWORD');
          }
        }
        throw new Error(data.error?.message || 'Processing failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  },

  analyzeHealthData: async (healthMetrics: any[], userContext?: any): Promise<any> => {
    try {
      const response = await fetch(`${API_BASE_URL}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          health_metrics: healthMetrics,
          user_context: userContext || {}
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('UNAUTHORIZED - Please log in again');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error?.message || 'Analysis failed');
      }

      return data;
    } catch (error) {
      console.error('Analysis API Error:', error);
      throw error;
    }
  },
  
  checkHealth: async (): Promise<{ success: boolean; status: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/system/health`, {
        method: 'GET',
        // No auth headers needed for health check
      });
      
      if (!response.ok) {
        return { success: false, status: 'unhealthy' };
      }
      
      const data = await response.json();
      
      return {
        success: data.success || false,
        status: data.data?.status || 'unknown'
      };
    } catch (error) {
      console.error('Health check failed:', error);
      return { success: false, status: 'disconnected' };
    }
  }
};